<template>
  <div v-if="show" class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
      <!-- 背景遮罩 -->
      <div 
        v-if="show"
        class="fixed inset-0 transition-opacity"
        @click="closeModal"
      >
        <div class="absolute inset-0 bg-gray-500 opacity-75"></div>
      </div>

      <!-- 模态框内容 -->
      <div class="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full sm:p-6 w-[90vw] max-w-[90vw] mx-auto">
        <!-- 标题 -->
        <div class="flex items-center justify-between mb-4">
          <div>
            <h3 class="text-lg font-medium text-gray-900">
              客户数据导出
            </h3>
            <p class="mt-1 text-sm text-gray-500">
              导出客户数据为Excel或CSV格式，支持自定义导出范围和格式
            </p>
          </div>
          <button
            @click="closeModal"
            class="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XMarkIcon class="h-6 w-6" />
          </button>
        </div>

        <!-- 导出范围选择 -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3">导出范围</label>
          <div class="space-y-3">
            <div class="flex items-center">
              <input
                id="export-all"
                v-model="exportRange"
                value="all"
                type="radio"
                class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              >
              <label for="export-all" class="ml-3 text-sm text-gray-700">
                导出全部客户 
                <span v-if="totalCount > 0" class="text-gray-500">(共 {{ totalCount }} 个)</span>
              </label>
            </div>
            
            <div class="flex items-center">
              <input
                id="export-filtered"
                v-model="exportRange"
                value="filtered"
                type="radio"
                class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                :disabled="!hasFilters"
              >
              <label for="export-filtered" :class="hasFilters ? 'text-gray-700' : 'text-gray-400'" class="ml-3 text-sm">
                导出当前筛选结果
                <span v-if="hasFilters && filteredCount > 0" class="text-gray-500">(共 {{ filteredCount }} 个)</span>
                <span v-if="!hasFilters" class="text-gray-400">(当前无筛选条件)</span>
              </label>
            </div>
            
            <div class="flex items-center">
              <input
                id="export-selected"
                v-model="exportRange"
                value="selected"
                type="radio"
                class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                :disabled="selectedCustomers.length === 0"
              >
              <label for="export-selected" :class="selectedCustomers.length > 0 ? 'text-gray-700' : 'text-gray-400'" class="ml-3 text-sm">
                导出已选择的客户
                <span v-if="selectedCustomers.length > 0" class="text-gray-500">(共 {{ selectedCustomers.length }} 个)</span>
                <span v-else class="text-gray-400">(请先选择客户)</span>
              </label>
            </div>
          </div>
        </div>

        <!-- 导出格式选择 -->
        <div class="mb-6">
          <label class="block text-sm font-medium text-gray-700 mb-3">导出格式</label>
          <div class="space-y-3">
            <div class="flex items-center">
              <input
                id="format-excel"
                v-model="exportFormat"
                value="excel"
                type="radio"
                class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              >
              <label for="format-excel" class="ml-3 text-sm text-gray-700">
                Excel格式 (.xlsx)
                <span class="text-xs text-gray-500 block">推荐，支持更好的格式化和中文显示</span>
              </label>
            </div>
            
            <div class="flex items-center">
              <input
                id="format-csv"
                v-model="exportFormat"
                value="csv"
                type="radio"
                class="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
              >
              <label for="format-csv" class="ml-3 text-sm text-gray-700">
                CSV格式 (.csv)
                <span class="text-xs text-gray-500 block">通用格式，可用于其他系统导入</span>
              </label>
            </div>
          </div>
        </div>

        <!-- 操作按钮 -->
        <div class="flex justify-end space-x-3 pt-4 border-t border-gray-200">
          <button
            @click="closeModal"
            type="button"
            class="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20 transition-colors"
            :disabled="exporting"
          >
            取消
          </button>
          <button
            @click="handleConfirmExport"
            type="button"
            :disabled="exporting || !isValidExport"
            class="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center transition-colors"
          >
            <svg v-if="exporting" class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
              <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            {{ exporting ? '导出中...' : '确认导出' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { XMarkIcon } from '@heroicons/vue/24/outline'

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },
  totalCount: {
    type: Number,
    default: 0
  },
  filteredCount: {
    type: Number,
    default: 0
  },
  selectedCustomers: {
    type: Array,
    default: () => []
  },
  hasFilters: {
    type: Boolean,
    default: false
  }
})

const emit = defineEmits(['close', 'export'])

const exportRange = ref('all')
const exportFormat = ref('excel')
const exporting = ref(false)

// 计算属性
const isValidExport = computed(() => {
  if (exportRange.value === 'selected') {
    return props.selectedCustomers.length > 0
  }
  if (exportRange.value === 'filtered') {
    return props.hasFilters && props.filteredCount > 0
  }
  return props.totalCount > 0
})

// 方法
const closeModal = () => {
  if (!exporting.value) {
    emit('close')
  }
}

const handleConfirmExport = () => {
  if (!isValidExport.value || exporting.value) return
  
  exporting.value = true
  emit('export', {
    range: exportRange.value,
    format: exportFormat.value,
    callback: () => {
      exporting.value = false
      emit('close')
    }
  })
}

// 重置表单状态
const resetForm = () => {
  exportRange.value = 'all'
  exportFormat.value = 'excel'
  exporting.value = false
}

// 暴露给父组件的方法
defineExpose({
  resetForm
})
</script>
